import { getSecureFileDownloadUrl } from "./constants";
import base from "../middlewares/interceptors";
import { toast } from "react-toastify";

/**
 * Detects the path type (Windows or Linux) based on path structure
 * @param filepath - The file path to analyze
 * @returns 'windows' | 'linux'
 */
const detectPathType = (filepath: string): "windows" | "linux" => {
  // Check for Windows drive letter pattern (C:, D:, etc.)
  if (/^[A-Za-z]:/.test(filepath)) {
    return "windows";
  }
  // Check for backslashes (Windows)
  if (filepath.includes("\\")) {
    return "windows";
  }
  // Default to Linux for forward slashes or ambiguous paths
  return "linux";
};

/**
 * Normalizes path separators to forward slashes for cross-platform compatibility
 * @param filepath - The file path to normalize
 * @returns Path with forward slashes
 */
const normalizePathSeparators = (filepath: string): string => {
  return filepath.replace(/\\/g, "/");
};

/**
 * Extracts the relevant path portion after 'download-files' marker
 * Handles both Windows (C:\path\to\download-files\file) and Linux (/path/to/download-files/file) paths
 * @param filepath - The full file path (Windows or Linux format)
 * @returns The extracted path portion after 'download-files'
 */
const extractRelevantPath = (filepath: string): string => {
  const normalizedPath = normalizePathSeparators(filepath);
  const marker = "download-files";
  const markerIndex = normalizedPath.indexOf(marker);

  if (markerIndex === -1) {
    // If no 'download-files' marker found, return the entire path
    return normalizedPath;
  }

  // Extract everything after 'download-files/'
  const startIndex = markerIndex + marker.length;
  let relevantPath = normalizedPath.substring(startIndex);

  // Remove leading slash if present
  if (relevantPath.startsWith("/")) {
    relevantPath = relevantPath.substring(1);
  }

  return relevantPath;
};

/**
 * Encodes path segments individually for URL safety while preserving path structure
 * @param filepath - The file path to encode
 * @returns URL-safe encoded path
 */
const encodePathForUrl = (filepath: string): string => {
  // Split by forward slash, encode each segment, then rejoin
  const segments = filepath.split("/").filter((segment) => segment.length > 0);
  const encodedSegments = segments.map((segment) =>
    encodeURIComponent(segment),
  );
  return encodedSegments.join("/");
};

/**
 * Extracts the relevant path portion after 'download-files' from file paths
 * and URL encodes it for secure API endpoint usage.
 * Handles both Windows paths (C:\path\to\file) and Linux paths (/path/to/file)
 * in a cross-platform manner.
 * @param filepath - The full file path (Windows or Linux format)
 * @returns The extracted and URL-encoded path safe for use in URLs
 */
export const extractAndEncodeFilePath = (filepath: string): string => {
  if (!filepath || typeof filepath !== "string") {
    throw new Error("Invalid file path provided to extractAndEncodeFilePath");
  }

  // Detect path type for logging/debugging purposes
  const pathType = detectPathType(filepath);
  console.log(`📁 Processing ${pathType} path: ${filepath}`);

  // Extract relevant path after 'download-files' marker
  const relevantPath = extractRelevantPath(filepath);

  // Encode path segments for URL safety
  const encodedPath = encodePathForUrl(relevantPath);

  console.log(`🔗 Encoded path for URL: ${encodedPath}`);

  return encodedPath;
};

export const downloadFileSecurely = async (
  filepath: string,
  fileName?: string,
  isExtractAndEncodeFilePath: boolean = false,
  onProgress?: (progress: {
    loaded: number;
    total: number;
    percentage: number;
  }) => void,
  maxRetries: number = 2,
): Promise<void> => {
  let lastError: any = null;

  // Retry logic for failed downloads
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        // Wait before retry (exponential backoff)
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, attempt) * 1000),
        );
      }

      await performDownload(
        filepath,
        fileName,
        isExtractAndEncodeFilePath,
        onProgress,
      );
      return; // Success - exit retry loop
    } catch (error) {
      lastError = error;
      console.error(`❌ Download attempt ${attempt + 1} failed:`, error);

      // Don't retry for certain error types
      const errorObj = error as any;
      if (
        errorObj?.response?.status === 404 || // File not found
        errorObj?.response?.status === 403 || // Access denied
        errorObj?.response?.status === 401 || // Unauthorized
        errorObj?.message?.includes("Invalid file path") ||
        errorObj?.message?.includes("Unable to process file path")
      ) {
        break;
      }

      if (attempt === maxRetries) {
        console.log(`🚫 Max retries (${maxRetries}) reached for: ${filepath}`);
        break;
      }
    }
  }

  // If we get here, all attempts failed
  // Show user-friendly error message only once after all retries are exhausted
  if (lastError) {
    const errorMessage = getDownloadErrorMessage(lastError);
    toast.error(errorMessage);
  }
  throw lastError;
};

/**
 * Performs the actual download operation
 * Handles cross-platform path processing for both Windows and Linux server environments
 */
const performDownload = async (
  filepath: string,
  fileName: string | undefined,
  isExtractAndEncodeFilePath: boolean,
  onProgress:
    | ((progress: {
        loaded: number;
        total: number;
        percentage: number;
      }) => void)
    | undefined,
): Promise<void> => {
  const startTime = Date.now();
  let finalFileName = "";
  try {
    // Input validation
    if (!filepath || typeof filepath !== "string") {
      throw new Error("Invalid file path provided");
    }

    // Process path with cross-platform normalization and encoding
    // This handles both Windows (C:\path\to\file) and Linux (/path/to/file) paths
    const encodedPath: string = isExtractAndEncodeFilePath
      ? extractAndEncodeFilePath(filepath)
      : filepath;

    if (!encodedPath) {
      throw new Error("Unable to process file path");
    }

    finalFileName = fileName || extractFileNameFromPath(filepath);

    const apiUrl = `${getSecureFileDownloadUrl}/${encodedPath}`;
    // const apiUrl = `${getSecureFileDownloadUrl}${encodedPath}`;

    // Enhanced timeout calculation based on estimated file size
    const baseTimeout = 120000; // 2 minutes base (increased from 1 minute)
    const maxTimeout = 900000; // 15 minutes max (increased from 10 minutes)
    const dynamicTimeout = Math.min(maxTimeout, baseTimeout);

    const response = await base.get(apiUrl, {
      responseType: "blob",
      headers: {
        accept: "application/octet-stream, application/json",
        "Cache-Control": "no-cache",
      },
      timeout: dynamicTimeout,
      onDownloadProgress: (progressEvent: any) => {
        if (onProgress) {
          // Handle different progress event structures
          let loaded = 0;
          let total = 0;
          let percentage = 0;

          // Check for standard XMLHttpRequest structure
          if (progressEvent.lengthComputable) {
            loaded = progressEvent.loaded || 0;
            total = progressEvent.total || 0;
            percentage = total > 0 ? Math.round((loaded * 100) / total) : 0;
          }
          // Check for axios/custom structure
          else if (
            progressEvent.loaded !== undefined &&
            progressEvent.total !== undefined
          ) {
            loaded = progressEvent.loaded || 0;
            total = progressEvent.total || 0;
            percentage = total > 0 ? Math.round((loaded * 100) / total) : 0;
          }
          // Check if percentage is already calculated
          else if (progressEvent.progress !== undefined) {
            loaded = progressEvent.bytes || progressEvent.loaded || 0;
            total = progressEvent.total || 0;
            percentage = Math.round(progressEvent.progress * 100);
          }

          // Only call onProgress if we have valid data
          if (total > 0 && loaded >= 0) {
            console.log(
              `📊 Progress: ${percentage}% (${loaded}/${total} bytes)`,
            );
            onProgress({ loaded, total, percentage });
          } else {
            console.warn("Invalid progress data:", {
              loaded,
              total,
              percentage,
              progressEvent,
            });
          }
        }
      },
      // Additional axios config for large files
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });

    // Validate response
    if (!response.data) {
      throw new Error("No data received from server");
    }

    if (!(response.data instanceof Blob)) {
      throw new Error("Invalid response format - expected blob");
    }

    const fileSize = response.data.size;

    // Check for browser blob size limitations
    // Get max file size from environment variable or use default

    const maxBlobSize = 500 * 1024 * 1024;

    if (fileSize > maxBlobSize) {
      console.warn(
        `⚠️ Large file detected (${(fileSize / (1024 * 1024)).toFixed(
          2,
        )} MB). Using alternative download method.`,
      );
      await downloadLargeFileAlternative(response.data, finalFileName);
    } else {
      await downloadStandardFile(response.data, finalFileName);
    }

    // Show success message with file size info
    toast.success(
      `File "${finalFileName}" downloaded successfully (${(
        fileSize /
        (1024 * 1024)
      ).toFixed(2)} MB)`,
    );
  } catch (error: any) {
    const downloadTime = Date.now() - startTime;
    console.error(`❌ Download failed after ${downloadTime}ms:`, error);

    // Enhanced error logging
    logDownloadError(error, filepath, finalFileName, downloadTime);

    // Don't show toast here - let the retry logic in downloadFileSecurely handle it
    throw error;
  }
};

/**
 * Standard file download method for smaller files
 * @param blob - The file blob data
 * @param fileName - The filename for download
 */
const downloadStandardFile = async (
  blob: Blob,
  fileName: string,
): Promise<void> => {
  try {
    const url = window.URL.createObjectURL(blob);
    const downloadElement = document.createElement("a");

    downloadElement.href = url;
    downloadElement.download = fileName;
    downloadElement.style.display = "none";

    document.body.appendChild(downloadElement);
    downloadElement.click();

    // Cleanup
    document.body.removeChild(downloadElement);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Standard download failed:", error);
    throw new Error("Failed to download file using standard method");
  }
};

/**
 * Alternative download method for very large files
 * Uses streaming approach to avoid memory issues
 * @param blob - The file blob data
 * @param fileName - The filename for download
 */
const downloadLargeFileAlternative = async (
  blob: Blob,
  fileName: string,
): Promise<void> => {
  try {
    // For very large files, we can use the File System Access API if available
    if ("showSaveFilePicker" in window) {
      await downloadWithFileSystemAPI(blob, fileName);
    } else {
      await downloadInChunks(blob, fileName);
    }
  } catch (error) {
    console.error("Large file download failed:", error);
    // Fallback to standard method

    await downloadStandardFile(blob, fileName);
  }
};

/**
 * Download using File System Access API (Chrome 86+)
 * @param blob - The file blob data
 * @param fileName - The filename for download
 */
const downloadWithFileSystemAPI = async (
  blob: Blob,
  fileName: string,
): Promise<void> => {
  try {
    const fileHandle = await (window as any).showSaveFilePicker({
      suggestedName: fileName,
      types: [
        {
          description: "All files",
          accept: { "*/*": [] },
        },
      ],
    });

    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
  } catch (error) {
    if ((error as any).name === "AbortError") {
      throw new Error("Download cancelled by user");
    }
    throw error;
  }
};

/**
 * Download large files in chunks to avoid memory issues
 * @param blob - The file blob data
 * @param fileName - The filename for download
 */
const downloadInChunks = async (
  blob: Blob,
  fileName: string,
): Promise<void> => {
  await downloadStandardFile(blob, fileName);
};

const logDownloadError = (
  error: any,
  filepath: string,
  fileName: string,
  downloadTime: number,
): void => {
  const errorDetails = {
    timestamp: new Date().toISOString(),
    filepath,
    fileName,
    downloadTime,
    errorType: error?.constructor?.name || "Unknown",
    errorMessage: error?.message || "Unknown error",
    httpStatus: error?.response?.status || null,
    httpStatusText: error?.response?.statusText || null,
    responseSize: error?.response?.data?.size || null,
    isTimeout:
      error?.code === "ECONNABORTED" ||
      error?.message?.includes("timeout") ||
      error?.message?.includes("Network Error") ||
      (error?.constructor?.name === "TypeError" && downloadTime >= 60000),
    isNetworkError: error?.code === "NETWORK_ERROR" || !navigator.onLine,
    userAgent: navigator.userAgent,
    connectionType: (navigator as any)?.connection?.effectiveType,
    memoryInfo: (performance as any)?.memory
      ? {
          usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
          totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
          jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit,
        }
      : null,
  };

  console.error("📊 Download Error Details:", errorDetails);

  if (typeof window !== "undefined" && (window as any).analytics) {
    (window as any).analytics.track("Download Failed", errorDetails);
  }
};

const getDownloadErrorMessage = (error: any): string => {
  if (
    error?.code === "ECONNABORTED" ||
    error?.message?.includes("timeout") ||
    error?.message?.includes("Network Error") ||
    (error?.constructor?.name === "TypeError" &&
      error?.message?.includes("Cannot read properties"))
  ) {
    return "Download timed out or network error occurred. The file may be too large or your connection is slow. Please try again.";
  }

  // Network errors
  if (error?.code === "NETWORK_ERROR" || !navigator.onLine) {
    return "Network error. Please check your internet connection and try again.";
  }

  // HTTP status errors (only if response exists)
  if (error?.response?.status) {
    const status = error.response.status;
    if (status === 404) {
      return "File not found. The file may have been moved or deleted.";
    } else if (status === 403) {
      return "Access denied. You don't have permission to download this file.";
    } else if (status === 401) {
      return "Authentication required. Please log in and try again.";
    } else if (status === 413) {
      return "File too large. The file exceeds the maximum download size limit.";
    } else if (status >= 500) {
      return "Server error. Please try again later or contact support.";
    }
  }

  // Memory errors
  if (error?.message?.includes("memory") || error?.message?.includes("heap")) {
    return "Insufficient memory to download this file. Try closing other tabs and try again.";
  }

  // Browser limitations
  if (error?.message?.includes("blob") || error?.message?.includes("size")) {
    return "File too large for browser to handle. Please try downloading in smaller chunks.";
  }

  // Generic fallback
  return "Download failed. Please try again or contact support if the problem persists.";
};

/**
 * Extracts filename from a file path (handles both Windows and Unix paths)
 * @param filepath - The full file path
 * @returns The filename
 */
export const extractFileNameFromPath = (filepath: string): string => {
  const normalizedPath = filepath.replace(/\\\\/g, "\\");
  const parts = normalizedPath.split(/[\\/]/);
  return parts[parts.length - 1] || "downloaded-file";
};

export const getSecureValidationReportPath = (filepath: string) => {
  const fileName = extractFileNameFromPath(filepath);

  return {
    downloadAction: () => downloadFileSecurely(filepath, fileName),
    fileName,
  };
};

export const getSecureCrossFieldValidationReportPaths = (filepath: string) => {
  const fileName = extractFileNameFromPath(filepath);

  return {
    downloadAction: () => downloadFileSecurely(filepath, fileName),
    fileName,
  };
};
